'use client';
import React, { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import AppLayout from '@/components/AppLayout';
import { createClient } from '@/lib/supabase/client';
import { Search, Star, CheckCircle, Loader2, BarChart3, Trophy, ChevronDown, X, Filter } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeveloperSummary {
  id: string;
  slug: string;
  name: string;
  logo_url: string | null;
  logo_alt: string | null;
  headquarters: string | null;
  reputation_score: number;
  avg_customer_rating: number;
  total_ratings: number;
  active_projects: number;
  completed_projects: number;
  total_units_delivered: number;
  on_time_delivery_pct: number;
  micro_markets: string[];
}

interface DeveloperProject {
  developer_id: string;
  developer_name: string;
  name: string;
  sold_units: number;
  total_units: number;
  status: string;
  possession_date: string;
  launch_year: number | null;
}

interface ChartDataPoint {
  developer: string;
  unitsSold: number;
  inventoryLaunched: number;
  unitsAvailable: number;
  possessionDelivered: number;
}

type TimePeriod = 'quarterly' | 'halfyearly' | 'yearly';

// All 30 default Pune micro market names (same as PuneMap defaultLocations)
const PUNE_DEFAULT_MICRO_MARKETS = [
  'Hinjewadi', 'Wakad', 'Baner', 'Balewadi', 'Aundh', 'Kothrud',
  'Pimpri', 'Chinchwad', 'Kharadi', 'Viman Nagar', 'Kalyani Nagar',
  'Hadapsar', 'Magarpatta', 'Wanowrie', 'Kondhwa', 'Undri', 'Wagholi',
  'NIBM', 'Sus', 'Tathawade', 'Punawale', 'Bavdhan', 'Pashan',
  'Shivajinagar', 'Koregaon Park', 'Camp', 'Yerawada', 'Lohegaon',
  'Dhanori', 'Manjri',
];

const LOGO_GRADIENTS = [
  'from-emerald-500 to-teal-600',
  'from-blue-500 to-indigo-600',
  'from-violet-500 to-purple-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-pink-600',
];

function getInitials(name: string): string {
  return name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

// ─── Star Rating Display ──────────────────────────────────────────────────────

function StarRating({ rating, size = 12 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={size}
          className={star <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground/30'}
        />
      ))}
    </div>
  );
}

// ─── Grade Badge ──────────────────────────────────────────────────────────────

function GradeBadge({ score }: { score: number }) {
  let grade = 'D';
  let color = 'text-red-400 bg-red-400/10 border-red-400/30';
  if (score >= 90) { grade = 'A+'; color = 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30'; }
  else if (score >= 80) { grade = 'A'; color = 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30'; }
  else if (score >= 70) { grade = 'B+'; color = 'text-amber-400 bg-amber-400/10 border-amber-400/30'; }
  else if (score >= 60) { grade = 'B'; color = 'text-amber-400 bg-amber-400/10 border-amber-400/30'; }
  else if (score >= 50) { grade = 'C'; color = 'text-orange-400 bg-orange-400/10 border-orange-400/30'; }

  return (
    <span className={`text-xs font-bold px-2 py-0.5 rounded border ${color}`}>{grade}</span>
  );
}

// ─── Custom Tooltip for Chart ─────────────────────────────────────────────────

function CustomChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-lg p-3 shadow-xl text-xs">
      <p className="font-semibold text-foreground mb-2 truncate max-w-[160px]">{label}</p>
      {payload.map((entry: any) => (
        <div key={entry.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: entry.color }} />
          <span className="text-muted-foreground">{entry.name}:</span>
          <span className="font-medium text-foreground">{entry.value.toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Developer Result Card (shown when filters applied) ───────────────────────

function DeveloperResultCard({ dev, onClick }: { dev: DeveloperSummary; onClick: () => void }) {
  const gradient = LOGO_GRADIENTS[dev.name.charCodeAt(0) % LOGO_GRADIENTS.length];
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-4 bg-card border border-border rounded-xl hover:border-amber-500/40 hover:bg-muted/20 transition-all text-left"
    >
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0 overflow-hidden shadow-md`}>
        {dev.logo_url ? (
          <img src={dev.logo_url} alt={dev.logo_alt || dev.name} className="w-full h-full object-contain p-0.5" />
        ) : (
          <span className="text-white font-bold text-xs">{getInitials(dev.name)}</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm truncate">{dev.name}</p>
        <p className="text-xs text-muted-foreground truncate">{dev.headquarters || 'Pune'}</p>
        {dev.micro_markets?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {dev.micro_markets.slice(0, 3).map((m) => (
              <span key={m} className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded">{m}</span>
            ))}
            {dev.micro_markets.length > 3 && (
              <span className="text-[10px] text-muted-foreground">+{dev.micro_markets.length - 3} more</span>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1.5 shrink-0">
        <StarRating rating={Number(dev.avg_customer_rating ?? 0)} size={12} />
        <GradeBadge score={dev.reputation_score ?? 0} />
      </div>
      <span className="text-xs text-amber-400 font-medium shrink-0">View →</span>
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function DevelopersDirectoryPage() {
  const router = useRouter();
  const supabase = createClient();

  // Search & filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [filterMicroMarket, setFilterMicroMarket] = useState('');
  const [filterProjectName, setFilterProjectName] = useState('');
  const [filterUnitType, setFilterUnitType] = useState('');
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Data state
  const [developers, setDevelopers] = useState<DeveloperSummary[]>([]);
  const [topRatedDevelopers, setTopRatedDevelopers] = useState<DeveloperSummary[]>([]);
  const [developerProjects, setDeveloperProjects] = useState<DeveloperProject[]>([]);
  const [microMarkets, setMicroMarkets] = useState<string[]>([]);
  const [projectNames, setProjectNames] = useState<string[]>([]);
  const [unitTypes, setUnitTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartPeriod, setChartPeriod] = useState<TimePeriod>('yearly');

  // Map: developer name (lowercase) → set of micro markets from projects table
  const [devNameToMicroMarkets, setDevNameToMicroMarkets] = useState<Map<string, Set<string>>>(new Map());
  // Map: developer name (lowercase) → set of price_per_sqft values from projects table
  const [devNameToPrices, setDevNameToPrices] = useState<Map<string, number[]>>(new Map());
  // Map: developer name (lowercase) → set of unit types from unit_inventory (via projects)
  const [devNameToUnitTypes, setDevNameToUnitTypes] = useState<Map<string, Set<string>>>(new Map());

  // Search results for redirect
  const [searchResults, setSearchResults] = useState<DeveloperSummary[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch all developers
        const { data: devData } = await supabase
          .from('developers')
          .select('id, slug, name, logo_url, logo_alt, headquarters, reputation_score, avg_customer_rating, total_ratings, active_projects, completed_projects, total_units_delivered, on_time_delivery_pct, micro_markets')
          .order('reputation_score', { ascending: false });

        const devs: DeveloperSummary[] = (devData || []).map((d: any) => ({
          ...d,
          micro_markets: d.micro_markets || [],
        }));
        setDevelopers(devs);

        // Top 5 by avg_customer_rating (grade rating)
        const sorted = [...devs].sort((a, b) => (b.avg_customer_rating ?? 0) - (a.avg_customer_rating ?? 0));
        setTopRatedDevelopers(sorted.slice(0, 5));

        // ── Build micro market list from ALL sources ──────────────────────────
        const allMarketsSet = new Set<string>();

        // 1. Start with the 30 default Pune map locations (always present)
        PUNE_DEFAULT_MICRO_MARKETS.forEach((m) => allMarketsSet.add(m));

        // 2. Add from developers.micro_markets array
        devs.forEach((d) => (d.micro_markets || []).forEach((m: string) => {
          if (m?.trim()) allMarketsSet.add(m.trim());
        }));

        // 3. Fetch distinct micro_market values from projects table
        const { data: projectMicroMarkets } = await supabase
          .from('projects')
          .select('micro_market, developer_name')
          .not('micro_market', 'is', null)
          .neq('micro_market', '');
        (projectMicroMarkets || []).forEach((p: any) => {
          if (p.micro_market?.trim()) allMarketsSet.add(p.micro_market.trim());
        });

        // Build a map: developer name (lowercase) → Set of micro markets from projects table
        const nameToMarkets = new Map<string, Set<string>>();
        (projectMicroMarkets || []).forEach((p: any) => {
          if (p.developer_name?.trim() && p.micro_market?.trim()) {
            const key = p.developer_name.trim().toLowerCase();
            if (!nameToMarkets.has(key)) nameToMarkets.set(key, new Set());
            nameToMarkets.get(key)!.add(p.micro_market.trim());
          }
        });
        setDevNameToMicroMarkets(nameToMarkets);

        // 4. Fetch display_name from micro_market_stats (admin-created micro markets)
        const { data: mmStats } = await supabase
          .from('micro_market_stats')
          .select('display_name')
          .not('display_name', 'is', null)
          .neq('display_name', '');
        (mmStats || []).forEach((m: any) => {
          if (m.display_name?.trim()) allMarketsSet.add(m.display_name.trim());
        });

        setMicroMarkets(Array.from(allMarketsSet).sort());

        // Fetch developer_projects for chart
        const { data: projData } = await supabase
          .from('developer_projects')
          .select('developer_id, name, sold_units, total_units, status, possession_date, launch_year');

        // Map developer_id to developer name
        const devMap = new Map(devs.map((d) => [d.id, d.name]));
        const projects: DeveloperProject[] = (projData || []).map((p: any) => ({
          ...p,
          developer_name: devMap.get(p.developer_id) || 'Unknown',
        }));
        setDeveloperProjects(projects);
        setProjectNames(Array.from(new Set(projects.map((p) => p.name))).sort());

        // Fetch price data from projects table (linked by developer_name)
        const { data: projectPriceData } = await supabase
          .from('projects')
          .select('developer_name, price_per_sqft, id')
          .not('developer_name', 'is', null)
          .neq('developer_name', '');

        // Build map: developer name (lowercase) → array of price_per_sqft values
        const nameToPrices = new Map<string, number[]>();
        (projectPriceData || []).forEach((p: any) => {
          if (p.developer_name?.trim() && p.price_per_sqft > 0) {
            const key = p.developer_name.trim().toLowerCase();
            if (!nameToPrices.has(key)) nameToPrices.set(key, []);
            nameToPrices.get(key)!.push(p.price_per_sqft);
          }
        });
        setDevNameToPrices(nameToPrices);

        // Build a map: project_id → developer_name (from projects table)
        const projectIdToDevName = new Map<string, string>();
        (projectPriceData || []).forEach((p: any) => {
          if (p.id && p.developer_name?.trim()) {
            projectIdToDevName.set(p.id, p.developer_name.trim().toLowerCase());
          }
        });

        // Fetch unit types from unit_inventory (linked to projects via project_id)
        const { data: unitData } = await supabase
          .from('unit_inventory')
          .select('unit_type, project_id')
          .not('unit_type', 'is', null)
          .neq('unit_type', '');

        // Build map: developer name (lowercase) → Set of unit types
        const nameToUnitTypes = new Map<string, Set<string>>();
        (unitData || []).forEach((u: any) => {
          if (u.unit_type?.trim() && u.project_id) {
            const devName = projectIdToDevName.get(u.project_id);
            if (devName) {
              if (!nameToUnitTypes.has(devName)) nameToUnitTypes.set(devName, new Set());
              nameToUnitTypes.get(devName)!.add(u.unit_type.trim());
            }
          }
        });
        setDevNameToUnitTypes(nameToUnitTypes);

        const types = Array.from(new Set((unitData || []).map((u: any) => u.unit_type))).sort();
        setUnitTypes(types as string[]);

      } catch (err) {
        console.error('Error fetching developer data:', err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Search dropdown logic
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      return;
    }
    const q = searchQuery.toLowerCase();
    const results = developers.filter((dev) =>
      dev.name.toLowerCase().includes(q) ||
      (dev.headquarters ?? '').toLowerCase().includes(q) ||
      (dev.micro_markets ?? []).some((m: string) => m.toLowerCase().includes(q))
    ).slice(0, 8);
    setSearchResults(results);
    setShowDropdown(results.length > 0);
  }, [searchQuery, developers]);

  // Chart data: aggregate top 5 developers by reputation_score
  const chartData: ChartDataPoint[] = useMemo(() => {
    const top5 = developers.slice(0, 5);
    const now = new Date();
    const currentYear = now.getFullYear();

    return top5.map((dev) => {
      let relevantProjects = developerProjects.filter((p) => p.developer_id === dev.id);

      // Filter by time period
      if (chartPeriod === 'quarterly') {
        const cutoff = currentYear - 1;
        relevantProjects = relevantProjects.filter((p) => (p.launch_year ?? 0) >= cutoff);
      } else if (chartPeriod === 'halfyearly') {
        const cutoff = currentYear - 2;
        relevantProjects = relevantProjects.filter((p) => (p.launch_year ?? 0) >= cutoff);
      }

      const unitsSold = relevantProjects.reduce((s, p) => s + (p.sold_units ?? 0), 0);
      const inventoryLaunched = relevantProjects.reduce((s, p) => s + (p.total_units ?? 0), 0);
      const unitsAvailable = Math.max(0, inventoryLaunched - unitsSold);
      const possessionDelivered = relevantProjects
        .filter((p) => p.status === 'Delivered' || (p.possession_date && new Date(p.possession_date) <= now))
        .reduce((s, p) => s + (p.total_units ?? 0), 0);

      return {
        developer: dev.name.length > 14 ? dev.name.slice(0, 14) + '…' : dev.name,
        unitsSold: unitsSold || dev.total_units_delivered || 0,
        inventoryLaunched: inventoryLaunched || (dev.active_projects + dev.completed_projects) * 50,
        unitsAvailable: unitsAvailable,
        possessionDelivered: possessionDelivered || dev.completed_projects * 40,
      };
    });
  }, [developers, developerProjects, chartPeriod]);

  // ── Filtered developers (shown when any filter is active) ─────────────────
  const filteredDevelopers = useMemo(() => {
    if (!filterMicroMarket && !filterProjectName && !filterUnitType && !filterPriceMin && !filterPriceMax) {
      return [];
    }

    return developers.filter((dev) => {
      // Micro market filter: check developer's micro_markets array AND projects table micro markets
      if (filterMicroMarket) {
        const selectedMarket = filterMicroMarket.toLowerCase();

        // Check 1: developer's own micro_markets array
        const devMarkets = (dev.micro_markets ?? []).map((m: string) => m.toLowerCase());
        const inDevArray = devMarkets.some((m) => m === selectedMarket || m.includes(selectedMarket) || selectedMarket.includes(m));

        // Check 2: projects table — match by developer name
        const projectsMarkets = devNameToMicroMarkets.get(dev.name.trim().toLowerCase());
        const inProjectsTable = projectsMarkets
          ? Array.from(projectsMarkets).some((m) => m.toLowerCase() === selectedMarket || m.toLowerCase().includes(selectedMarket) || selectedMarket.includes(m.toLowerCase()))
          : false;

        if (!inDevArray && !inProjectsTable) return false;
      }

      // Project name filter: check if developer has a matching project
      if (filterProjectName) {
        const devProjects = developerProjects.filter((p) => p.developer_id === dev.id);
        if (!devProjects.some((p) => p.name.toLowerCase().includes(filterProjectName.toLowerCase()))) return false;
      }

      // Unit type filter: check unit_inventory via projects table linked by developer_name
      if (filterUnitType) {
        const devUnitTypes = devNameToUnitTypes.get(dev.name.trim().toLowerCase());
        if (!devUnitTypes || !devUnitTypes.has(filterUnitType)) return false;
      }

      // Price range filter: check projects table price_per_sqft linked by developer_name
      if (filterPriceMin || filterPriceMax) {
        const devPrices = devNameToPrices.get(dev.name.trim().toLowerCase());
        if (!devPrices || devPrices.length === 0) return false;
        const hasMatchingPrice = devPrices.some((price) => {
          if (filterPriceMin && price < Number(filterPriceMin)) return false;
          if (filterPriceMax && price > Number(filterPriceMax)) return false;
          return true;
        });
        if (!hasMatchingPrice) return false;
      }

      return true;
    });
  }, [developers, developerProjects, devNameToMicroMarkets, devNameToUnitTypes, devNameToPrices, filterMicroMarket, filterProjectName, filterUnitType, filterPriceMin, filterPriceMax]);

  const hasActiveFilters = filterMicroMarket || filterProjectName || filterUnitType || filterPriceMin || filterPriceMax;

  function clearFilters() {
    setFilterMicroMarket('');
    setFilterProjectName('');
    setFilterUnitType('');
    setFilterPriceMin('');
    setFilterPriceMax('');
  }

  function handleSelectDeveloper(dev: DeveloperSummary) {
    setShowDropdown(false);
    setSearchQuery('');
    router.push(`/developer-detail?slug=${dev.slug}`);
  }

  return (
    <AppLayout activeRoute="/developers">
      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Developers Directory</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Browse verified real estate developers — track records, reputation scores, and project portfolios.
          </p>
        </div>

        {/* ── Search + Filters Row ── */}
        <div className="space-y-3">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search Box */}
            <div className="relative flex-1 max-w-xl">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                placeholder="Search developers by name, market, location..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                className="w-full pl-9 pr-4 py-2.5 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/50 transition-colors"
              />
              {/* Search Dropdown */}
              {showDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  {searchResults.map((dev) => (
                    <button
                      key={dev.id}
                      onMouseDown={() => handleSelectDeveloper(dev)}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-muted/40 transition-colors text-left"
                    >
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${LOGO_GRADIENTS[0]} flex items-center justify-center shrink-0 overflow-hidden`}>
                        {dev.logo_url ? (
                          <img src={dev.logo_url} alt={dev.logo_alt || dev.name} className="w-full h-full object-contain p-0.5" />
                        ) : (
                          <span className="text-white font-bold text-xs">{getInitials(dev.name)}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{dev.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{dev.headquarters || 'Pune'}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Star size={10} className="text-amber-400 fill-amber-400" />
                        <span className="text-xs text-foreground">{Number(dev.avg_customer_rating ?? 0).toFixed(1)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Filter Toggle Button */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-amber-500/10 border-amber-500/40 text-amber-400' :'bg-card border-border text-muted-foreground hover:border-amber-500/30 hover:text-foreground'
              }`}
            >
              <Filter size={14} />
              Filters
              {hasActiveFilters && (
                <span className="bg-amber-500 text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                  {[filterMicroMarket, filterProjectName, filterUnitType, filterPriceMin || filterPriceMax].filter(Boolean).length}
                </span>
              )}
              <ChevronDown size={14} className={`transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Filter Panel */}
          {showFilters && (
            <div className="bg-card border border-border rounded-xl p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {/* Micro Market */}
                <div>
                  <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Micro Market</label>
                  <select
                    value={filterMicroMarket}
                    onChange={(e) => setFilterMicroMarket(e.target.value)}
                    className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-amber-500/50"
                  >
                    <option value="">All Markets</option>
                    {microMarkets.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>

                {/* Project Name */}
                <div>
                  <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Project Name</label>
                  <select
                    value={filterProjectName}
                    onChange={(e) => setFilterProjectName(e.target.value)}
                    className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-amber-500/50"
                  >
                    <option value="">All Projects</option>
                    {projectNames.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>

                {/* Unit Type */}
                <div>
                  <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Unit Type</label>
                  <select
                    value={filterUnitType}
                    onChange={(e) => setFilterUnitType(e.target.value)}
                    className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:border-amber-500/50"
                  >
                    <option value="">All Unit Types</option>
                    {unitTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>

                {/* Price Range */}
                <div>
                  <label className="text-xs text-muted-foreground font-medium mb-1.5 block">Price Range (₹)</label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Min"
                      value={filterPriceMin}
                      onChange={(e) => setFilterPriceMin(e.target.value)}
                      className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/50"
                    />
                    <input
                      type="number"
                      placeholder="Max"
                      value={filterPriceMax}
                      onChange={(e) => setFilterPriceMax(e.target.value)}
                      className="w-full bg-muted/30 border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-amber-500/50"
                    />
                  </div>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex items-center justify-between pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground">
                    {loading ? 'Loading...' : `${filteredDevelopers.length} developer${filteredDevelopers.length !== 1 ? 's' : ''} match your filters`}
                  </p>
                  <button
                    onClick={clearFilters}
                    className="flex items-center gap-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                  >
                    <X size={12} />
                    Clear all
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Filter Results ── */}
          {hasActiveFilters && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Search size={14} className="text-amber-400" />
                <h3 className="text-sm font-semibold text-foreground">
                  Filter Results
                  <span className="ml-2 text-xs font-normal text-muted-foreground">
                    ({filteredDevelopers.length} developer{filteredDevelopers.length !== 1 ? 's' : ''} found)
                  </span>
                </h3>
              </div>

              {loading ? (
                <div className="flex items-center justify-center py-10 bg-card border border-border rounded-xl">
                  <Loader2 size={24} className="animate-spin text-amber-400" />
                </div>
              ) : filteredDevelopers.length > 0 ? (
                <div className="space-y-2">
                  {filteredDevelopers.map((dev) => (
                    <DeveloperResultCard
                      key={dev.id}
                      dev={dev}
                      onClick={() => router.push(`/developer-detail?slug=${dev.slug}`)}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-10 bg-card border border-border rounded-xl text-center">
                  <Search size={24} className="text-muted-foreground/40 mb-3" />
                  <p className="text-sm font-medium text-foreground mb-1">No developers found</p>
                  <p className="text-xs text-muted-foreground max-w-xs">
                    No developers match the selected filters. Try adjusting or clearing your filters.
                  </p>
                  <button
                    onClick={clearFilters}
                    className="mt-3 text-xs text-amber-400 hover:text-amber-300 transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Top 5 Developers Performance Chart ── */}
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2">
              <BarChart3 size={18} className="text-amber-400" />
              <h2 className="text-lg font-bold text-foreground">Top 5 Developers — Performance Chart</h2>
            </div>
            {/* Period Toggle */}
            <div className="sm:ml-auto flex items-center gap-1 bg-muted/30 border border-border rounded-lg p-1">
              {(['quarterly', 'halfyearly', 'yearly'] as TimePeriod[]).map((period) => (
                <button
                  key={period}
                  onClick={() => setChartPeriod(period)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    chartPeriod === period
                      ? 'bg-amber-500 text-black' :'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {period === 'quarterly' ? 'Quarterly' : period === 'halfyearly' ? 'Half Yearly' : 'Yearly'}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 bg-card border border-border rounded-xl">
              <Loader2 size={28} className="animate-spin text-amber-400" />
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-5">
              <div className="flex flex-wrap gap-4 mb-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-amber-400 inline-block" />Units Sold</div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block" />Inventory Launched</div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-400 inline-block" />Units Available</div>
                <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-400 inline-block" />Possession Delivered</div>
              </div>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis
                    dataKey="developer"
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={{ stroke: 'rgba(255,255,255,0.1)' }}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                  />
                  <Tooltip content={<CustomChartTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                  <Bar dataKey="unitsSold" name="Units Sold" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="inventoryLaunched" name="Inventory Launched" fill="#60a5fa" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="unitsAvailable" name="Units Available" fill="#34d399" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="possessionDelivered" name="Possession Delivered" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground/60 mt-2 text-center">
                Based on {chartPeriod === 'quarterly' ? 'last 1 year' : chartPeriod === 'halfyearly' ? 'last 2 years' : 'all time'} data from Supabase
              </p>
            </div>
          )}
        </div>

        {/* ── Top 5 Grade-Rated Developers ── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Trophy size={18} className="text-amber-400" />
            <h2 className="text-lg font-bold text-foreground">Top 5 Grade-Rated Developers</h2>
            <span className="ml-auto text-xs text-muted-foreground bg-muted/40 border border-border px-2.5 py-0.5 rounded-full">
              By Customer Rating
            </span>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 size={28} className="animate-spin text-amber-400" />
            </div>
          ) : topRatedDevelopers.length > 0 ? (
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              {topRatedDevelopers.map((dev, idx) => {
                const gradient = LOGO_GRADIENTS[idx % LOGO_GRADIENTS.length];
                const rankEmojis = ['🏆', '🥈', '🥉', '🎖️', '🎖️'];
                return (
                  <button
                    key={dev.id}
                    onClick={() => router.push(`/developer-detail?slug=${dev.slug}`)}
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-muted/20 transition-colors border-b border-border last:border-b-0 text-left"
                  >
                    {/* Rank */}
                    <div className="w-8 text-center shrink-0">
                      <span className="text-lg">{rankEmojis[idx]}</span>
                    </div>

                    {/* Logo */}
                    <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center shrink-0 overflow-hidden shadow-md`}>
                      {dev.logo_url ? (
                        <img src={dev.logo_url} alt={dev.logo_alt || dev.name} className="w-full h-full object-contain p-0.5" />
                      ) : (
                        <span className="text-white font-bold text-xs">{getInitials(dev.name)}</span>
                      )}
                    </div>

                    {/* Name + Location */}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground text-sm truncate">{dev.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{dev.headquarters || 'Pune'}</p>
                    </div>

                    {/* Star Rating */}
                    <div className="flex flex-col items-center gap-1 shrink-0">
                      <StarRating rating={Number(dev.avg_customer_rating ?? 0)} size={13} />
                      <span className="text-xs font-bold text-foreground">
                        {Number(dev.avg_customer_rating ?? 0).toFixed(1)}
                        <span className="text-muted-foreground font-normal">/5</span>
                      </span>
                    </div>

                    {/* Grade Badge */}
                    <div className="shrink-0">
                      <GradeBadge score={dev.reputation_score ?? 0} />
                    </div>

                    {/* Rep Score */}
                    <div className="hidden sm:flex flex-col items-end shrink-0">
                      <span className={`text-sm font-bold ${(dev.reputation_score ?? 0) >= 85 ? 'text-emerald-400' : (dev.reputation_score ?? 0) >= 70 ? 'text-amber-400' : 'text-foreground'}`}>
                        {dev.reputation_score ?? 0}
                      </span>
                      <span className="text-[10px] text-muted-foreground">Rep. Score</span>
                    </div>

                    {/* RERA + CTA */}
                    <div className="hidden md:flex items-center gap-3 shrink-0">
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <CheckCircle size={11} className="text-emerald-400" />
                        <span>RERA</span>
                      </div>
                      <span className="text-xs text-amber-400 font-medium">View →</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center py-10 text-sm text-muted-foreground bg-card border border-border rounded-xl">
              No developer rating data available yet.
            </div>
          )}
        </div>

        {/* ── Search Prompt (no listing cards) ── */}
        <div className="border-t border-border pt-6">
          <div className="flex flex-col items-center justify-center py-12 text-center bg-card border border-border rounded-xl">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-4">
              <Search size={28} className="text-amber-400" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-2">Find a Developer</h3>
            <p className="text-sm text-muted-foreground max-w-sm mb-4">
              Use the search bar above to find developers by name, micro market, or location. Or use the filters to narrow results by micro market, project, or unit type.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {microMarkets.slice(0, 6).map((m) => (
                <button
                  key={m}
                  onClick={() => { setFilterMicroMarket(m); setShowFilters(true); }}
                  className="text-xs bg-muted/40 hover:bg-amber-500/10 hover:text-amber-400 hover:border-amber-500/30 text-muted-foreground px-3 py-1.5 rounded-full border border-border transition-colors"
                >
                  {m}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground/60 mt-4">
              {developers.length} verified developers · {microMarkets.length} micro markets
            </p>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
